import { useMemo } from 'react';
import productsRaw from '../data/products.json';
import BrandCatalog from '../components/catalog/BrandCatalog';
import type { CatalogItem, BrandConfig } from '../components/catalog/BrandCatalog';
import { parseSpecs, parseFeatures, parseDescription } from '../utils/specParser';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import { useStructuredData } from '@/hooks/useStructuredData';
import { buildBrandCatalogSchema, buildBreadcrumbSchema } from '@/lib/seo/schemas';

interface CreavitRaw {
  id: string;
  title: string;
  category: string;
  sku: string;
  tags: string[];
  images: string[];
  description?: string;
}

/* ══════════════════════════════════════════════════════════════
   CREAVIT CATEGORY TAXONOMY
   rawCategory → { mainGroup, subCategory }
   ══════════════════════════════════════════════════════════════ */
type Mapping = { main: string; sub: string };

const CATEGORY_MAP: Record<string, Mapping> = {
  // ── VİTRİFİYELER → Klozetler ──────────────────────────────
  'Rim-Off Klozet':                   { main: 'Vitrifiyeler', sub: 'Asma Klozetler' },
  'Asma Klozet':                      { main: 'Vitrifiyeler', sub: 'Asma Klozetler' },
  'Takım Klozetler':                  { main: 'Vitrifiyeler', sub: 'Takım Klozetler' },
  'Duroplast Klozet Kapakları':       { main: 'Vitrifiyeler', sub: 'Klozet Kapakları' },
  'Thermoplast Klozet Kapakları':     { main: 'Vitrifiyeler', sub: 'Klozet Kapakları' },
  'Klozet Kapakları':                 { main: 'Vitrifiyeler', sub: 'Klozet Kapakları' },
  'Gömme Rezervuarlar':               { main: 'Vitrifiyeler', sub: 'Rezervuar ve İç Takımlar' },
  'İç Takım':                         { main: 'Vitrifiyeler', sub: 'Rezervuar ve İç Takımlar' },
  'Kumanda Panelleri':                { main: 'Vitrifiyeler', sub: 'Gömme Rezervuar ve Kumanda Paneli' },
  'Gömme Rezervuar':                  { main: 'Vitrifiyeler', sub: 'Gömme Rezervuar ve Kumanda Paneli' },

  // ── VİTRİFİYELER → Lavabolar ──────────────────────────────
  'Çanak Lavabo':                     { main: 'Vitrifiyeler', sub: 'Çanak Lavabolar' },
  'Tezgah Üstü Lavabo':               { main: 'Vitrifiyeler', sub: 'Tezgah Üstü Lavabolar' },
  'Etajerli Lavabo':                  { main: 'Vitrifiyeler', sub: 'Etajerli Lavabolar' },
  'Ayaklı Lavabo':                    { main: 'Vitrifiyeler', sub: 'Ayaklı Lavabolar' },
  'Tezgah Altı Lavabo':               { main: 'Vitrifiyeler', sub: 'Tezgah Altı Lavabolar' },

  // ── ARMATÜRLER → Batarya ve Musluklar ─────────────────────
  'Banyo Armatürü':                   { main: 'Armatürler', sub: 'Banyo Bataryası' },
  'Banyo Bataryası':                  { main: 'Armatürler', sub: 'Banyo Bataryası' },
  'Lavabo Armatürü':                  { main: 'Armatürler', sub: 'Lavabo Bataryası' },
  'Lavabo Bataryası':                 { main: 'Armatürler', sub: 'Lavabo Bataryası' },
  'Mutfak Armatürü':                  { main: 'Armatürler', sub: 'Mutfak Bataryası' },
  'Mutfak Bataryası':                 { main: 'Armatürler', sub: 'Mutfak Bataryası' },
  'Termostatik Banyo Armatürü':       { main: 'Armatürler', sub: 'Banyo Bataryası' },
  'Fotoselli Armatürler':             { main: 'Armatürler', sub: 'Musluklar' },
  'Ara Musluklar':                    { main: 'Armatürler', sub: 'Musluklar' },
  'Musluk':                           { main: 'Armatürler', sub: 'Musluklar' },

  // ── ARMATÜRLER → Duş Ürünleri ─────────────────────────────
  'Duş Setleri':                      { main: 'Armatürler', sub: 'Duş Seti' },
  'El Duş Seti':                      { main: 'Armatürler', sub: 'Duş Seti' },
  'Duş Sistemi':                      { main: 'Armatürler', sub: 'Duş Sistemi' },
  'Duş Başlığı':                      { main: 'Armatürler', sub: 'Duş Başlığı' },
  'Yer Sifonu':                       { main: 'Armatürler', sub: 'Yer Sifonu ve Duş Kanalları' },
  'Duş Kanalı':                       { main: 'Armatürler', sub: 'Yer Sifonu ve Duş Kanalları' },

  // ── BANYO MOBİLYALARI ─────────────────────────────────────
  'Çekmeceli Lavabo Dolabı':          { main: 'Banyo Mobilyaları', sub: 'Lavabo Dolapları' },
  'Kapaklı Lavabo Dolabı':            { main: 'Banyo Mobilyaları', sub: 'Lavabo Dolapları' },
  'Kapaksız Lavabo Dolabı':           { main: 'Banyo Mobilyaları', sub: 'Lavabo Dolapları' },
  'Banyo':                            { main: 'Banyo Mobilyaları', sub: 'Lavabo Dolapları' },
  'Aynalar':                          { main: 'Banyo Mobilyaları', sub: 'Üst Dolap ve Aynalar' },
  'Üst Dolaplar':                     { main: 'Banyo Mobilyaları', sub: 'Üst Dolap ve Aynalar' },
  'Boy Dolapları':                    { main: 'Banyo Mobilyaları', sub: 'Boy Dolapları' },

  // ── AKSESUARLAR ───────────────────────────────────────────
  'Diş Fırçalık':                     { main: 'Aksesuarlar', sub: 'Diş Fırçalık' },
  'Sabunluk':                         { main: 'Aksesuarlar', sub: 'Sabunluk' },
  'Tuvalet Fırçalığı':                { main: 'Aksesuarlar', sub: 'Tuvalet Fırçalığı' },
  'Tuvalet Kağıtlığı':               { main: 'Aksesuarlar', sub: 'Tuvalet Kağıtlığı' },
  'Bornozluk':                        { main: 'Aksesuarlar', sub: 'Bornoz Askıları' },
  'Havluluk':                         { main: 'Aksesuarlar', sub: 'Havlu Askıları' },
  'Kağıt Havluluk':                   { main: 'Aksesuarlar', sub: 'Havlu Askıları' },
  'Çöp Kovaları':                     { main: 'Aksesuarlar', sub: 'Çöp Kovaları' },
  'Lavabo Sifonları':                 { main: 'Aksesuarlar', sub: 'Lavabo Sifonları' },
  'Lavabo Sifon Başlığı':             { main: 'Aksesuarlar', sub: 'Lavabo Sifon Başlıkları' },
};

/* ── Model → Koleksiyon eşlemesi ── */
const COLLECTION_KEYWORDS: Record<string, string> = {
  'siyah':    'Siyah Elegant Ürünler',
  'elegant':  'Siyah Elegant Ürünler',
  'colorit':  'Renkli Ürünler ile Özgür Banyolar',
  'renkli':   'Renkli Ürünler ile Özgür Banyolar',
  'small':    'Fonksiyonel Küçük Banyolar',
  'mini':     'Fonksiyonel Küçük Banyolar',
  'free':     'Fonksiyonel Küçük Banyolar',
  'art':      'Yenilikçi Gen-Z Art Lavabolar',
  'loop':     'Yenilikçi Gen-Z Art Lavabolar',
};

const MODEL_KEYWORDS = ['D20', 'KAYRA', 'FREE', 'ELEGANT', 'LOOP', 'COLORIT', 'PATARA', 'SAILY'];

/* ── Resolve mapping ── */
function resolve(rawCat: string): Mapping {
  // Direct match
  if (CATEGORY_MAP[rawCat]) return CATEGORY_MAP[rawCat];
  // Partial / case-insensitive
  const lower = rawCat.toLowerCase();
  for (const [key, val] of Object.entries(CATEGORY_MAP)) {
    if (lower.includes(key.toLowerCase()) || key.toLowerCase().includes(lower)) return val;
  }
  // Numeric garbage
  if (/^\d+$/.test(rawCat.trim())) return { main: '', sub: '' };
  return { main: 'Diğer', sub: rawCat };
}

/* ── Model detector ── */
function detectModel(tags: string[], title: string): string {
  const combined = [...tags, title].join(' ').toUpperCase();
  for (const m of MODEL_KEYWORDS) {
    if (combined.includes(m)) return m.charAt(0) + m.slice(1).toLowerCase();
  }
  return '';
}

/* ── Collection detector ── */
function detectCollection(tags: string[], title: string): string {
  const combined = [...tags, title].join(' ').toLowerCase();
  for (const [kw, col] of Object.entries(COLLECTION_KEYWORDS)) {
    if (combined.includes(kw)) return col;
  }
  return '';
}

/* ── Map raw product → CatalogItem ── */
function mapToItem(p: CreavitRaw): CatalogItem | null {
  const { main, sub } = resolve(p.category || '');
  if (!main) return null; // skip garbage

  const rawHtml = p.description || '';
  const parsedSpecs = parseSpecs(rawHtml);
  const specs: Record<string, string> = {};
  for (const s of parsedSpecs) specs[s.label] = s.value;
  const features = parseFeatures(rawHtml);
  const cleanDesc = parseDescription(rawHtml);

  const model = detectModel(p.tags || [], p.title);
  const collection = detectCollection(p.tags || [], p.title);

  const chipFilters: Record<string, string> = {
    'Ana Grup': main,
    'Alt Kategori': sub,
  };
  if (model) chipFilters['Model'] = model;
  if (collection) chipFilters['Koleksiyon'] = collection;

  const cleanTags = (p.tags || []).filter(
    t => t && !/^(yaz-\d+|creavit)$/i.test(t.trim())
  );

  return {
    id: p.id,
    title: p.title,
    sku: p.sku,
    images: p.images || [],
    groupKey: main,
    groupLabel: main,
    subtitle: sub !== main ? sub : undefined,
    tags: cleanTags,
    chipFilters,
    specs: Object.keys(specs).length > 0 ? specs : undefined,
    features: features.length > 0 ? features : cleanTags.slice(0, 6),
    description: cleanDesc,
    rawDescription: rawHtml,
  };
}

/* ── Brand Config ── */
const creavitBrand: BrandConfig = {
  brandName: 'Creavit',
  tagline: 'Türkiye\'nin Önde Gelen Banyo Markası | Distribütör',
  accentColor: '#1a1a2e',
  accentLight: '#f0f4ff',
  accentMid: '#dde3f5',
  groupMode: 'flat',
  searchPlaceholder: 'Ürün adı, model veya SKU ara…',
  filterGroups: [
    { label: 'Ana Grup',     field: 'Ana Grup' },
    { label: 'Alt Kategori', field: 'Alt Kategori' },
    { label: 'Model',        field: 'Model' },
    { label: 'Koleksiyon',   field: 'Koleksiyon' },
  ],
};

export default function Products() {
  const items = useMemo<CatalogItem[]>(
    () =>
      (productsRaw as CreavitRaw[])
        .map(mapToItem)
        .filter((x): x is CatalogItem => x !== null),
    []
  );

  useSEO({
    title: 'Creavit Vitrifiye Diyarbakır | Banyo, Klozet, Lavabo Kataloğu — Mayi Mimarlık',
    description:
      "Diyarbakır Creavit bölge distribütörü Mayi Mimarlık. 889+ vitrifiye ürünü: klozet, lavabo, armatür, banyo mobilyası. Kayapınar showroom'da görün, online teklif alın.",
    keywords: localKeywords(
      'creavit diyarbakır',
      'creavit bayi diyarbakır',
      'vitrifiye diyarbakır',
      'banyo seti diyarbakır',
      'klozet lavabo diyarbakır',
      'creavit fiyat teklifi'
    ),
    canonical: 'https://mayimimarlik.com/urunler',
  });

  const schema = useMemo(() => {
    const catalog = buildBrandCatalogSchema({
      brandName: 'Creavit',
      description: 'Creavit vitrifiye ve banyo ürünleri — Diyarbakır bölge distribütörü kataloğu',
      url: 'https://mayimimarlik.com/urunler',
      productCount: items.length,
    });
    const breadcrumb = buildBreadcrumbSchema([
      { name: 'Ana Sayfa', url: 'https://mayimimarlik.com/' },
      { name: 'Creavit Vitrifiye', url: 'https://mayimimarlik.com/urunler' },
    ]);
    const { '@context': _a, ...cat } = catalog;
    const { '@context': _b, ...crumb } = breadcrumb;
    return { '@context': 'https://schema.org', '@graph': [cat, crumb] };
  }, [items.length]);

  useStructuredData('schema-creavit', schema);

  return <BrandCatalog config={creavitBrand} items={items} />;
}
